import dayjs from "dayjs";
import { re } from "mathjs";

import {
  abbreviate,
  formatCellPhoneNumber,
  formatCpf,
} from "../functions";

const EMPTY_CUSTOMER = {
  id: null,
  name: "",
  cpf: "",
  email: "",
  cellphone: "",
  telephone: "",
  address: null,
  createdAt: null,
  lastVisitAt: null,
};

function normalizeHistoryLabel(label) {
  const lowerLabel = (label || "").toLowerCase();
  switch (lowerLabel) {
    case 'racao seca':
      return 'Ração seca';
    case 'anti pulgas e carrapatos':
      return 'Anti pulgas e carrapatos';
    case 'tapete higienico':
      return 'Tapete higiênico';
    case 'petiscos':
      return 'Petiscos';
    case 'desinfetantes e tira cheiro':
      return 'Desinfetantes e tira cheiro';
    default:
      return label || "";
  }
}

function normalizeDate(value) {
  return value || null;
}

function normalizeHistory(items, labelKey) {
  if (!Array.isArray(items)) return [];

  const historyByLabel = new Map();

  items.forEach((item) => {
    const label = item?.[labelKey];
    if (!label) return;

    const current = historyByLabel.get(label);
    const nextDate = normalizeDate(item.date);
    const currentTime = current?.date ? new Date(current.date).getTime() : 0;
    const nextTime = nextDate ? new Date(nextDate).getTime() : 0;

    if (!current || nextTime >= currentTime) {
      historyByLabel.set(label, {
        [labelKey]: normalizeHistoryLabel(label),
        date: nextDate,
      });
    }
  });

  return Array.from(historyByLabel.values());
}

function formatAddress(address) {
  if (!address) return "";
  if (typeof address === "string") return address;

  const street = [address.street, address.number].filter(Boolean).join(", ");
  const city = [address.city, address.uf].filter(Boolean).join(" - ");

  return [
    street,
    address.complement,
    address.neighborhood,
    city,
    address.postalCode || address.zipCode,
  ].filter(Boolean).join(" · ");
}

function formatBirthdate(value) {
  if (!value) return "Data inválida";

  const birthdate = dayjs(value);
  return birthdate.isValid() ? birthdate.format("DD/MM/YYYY") : "Data inválida";
}

function formatAge(value) {
  if (!value) return "";

  const birthdate = dayjs(value);
  if (!birthdate.isValid() || birthdate.isAfter(dayjs())) return "";

  const today = dayjs();
  const years = today.diff(birthdate, "year");
  const afterYears = birthdate.add(years, "year");
  const months = today.diff(afterYears, "month");
  const afterMonths = afterYears.add(months, "month");
  const days = today.diff(afterMonths, "day");
  const ageParts = [];

  if (years) ageParts.push(`${years} ${years === 1 ? "ano" : "anos"}`);
  if (months) ageParts.push(`${months} ${months === 1 ? "mês" : "meses"}`);
  if (days) ageParts.push(`${days} ${days === 1 ? "dia" : "dias"}`);

  if (ageParts.length <= 1) return ageParts[0] || "";
  return `${ageParts.slice(0, -1).join(", ")} e ${ageParts[ageParts.length - 1]}`;
}

function hasPetItem(items, pet) {
  if (!Array.isArray(items)) return false;

  return items.some((item = {}) => {
    if (pet.id && item.petId) return item.petId === pet.id;
    return !!pet.name && item.petName === pet.name;
  });
}

function normalizePets(pets, customer, subscriptions, activePackages) {
  if (!Array.isArray(pets)) return [];

  return pets
    .map((pet = {}) => {
      const weight = Number(pet.weight).toFixed(3).replace(".", ",");
      const active = pet.active ?? true;

      return {
        id: pet.id || null,
        name: pet.name || "",
        photo: pet.photo || null,
        species: pet.species || "",
        breed: pet.breed || "",
        gender: pet.gender || "",
        size: pet.size || "",
        weight,
        birthdate: normalizeDate(pet.birthdate),
        castrated: pet.castrated ?? null,
        active,
        card: {
          badges: {
            alreadyClient:
              typeof pet.alreadyClient === "boolean"
                ? pet.alreadyClient
                : !!customer.id,
            subscription:
              typeof pet.subscription === "boolean"
                ? pet.subscription
                : hasPetItem(subscriptions, pet),
            packages:
              typeof pet.packages === "boolean"
                ? pet.packages
                : hasPetItem(activePackages, pet),
          },
          attributes: [
            { label: "Espécie", value: pet.species || "" },
            { label: "Raça", value: pet.breed || "" },
            { label: "Sexo", value: pet.gender || "" },
            { label: "Peso", value: pet.weight?.toString() || "" },
            { label: "Porte", value: pet.size || "" },
            { label: "Pelagem", value: pet.fur || pet.coat || "" },
            {
              label: "Castrado",
              value: pet.castrated == null ? "" : pet.castrated ? "Sim" : "Não",
            },
            { label: "Status", value: active ? "Vivo" : "Morto" },
            { label: "Nome", value: (pet.name || "").trim().split(" ")[0] || "", },
            { label: "Nascimento", value: formatBirthdate(pet.birthdate) },
            { label: "Idade", value: formatAge(pet.birthdate) },
            { label: "Tutor", value: abbreviate(customer.name) || "" },
            { label: "TutorFull", value: customer.name || "" },
            { label: "TutorCpf", value: formatCpf(customer.cpf) || "" },
            {
              label: "TutorPhone",
              value: formatCellPhoneNumber(customer.cellphone || customer.telephone),
            },
            { label: "TutorEmail", value: customer.email || undefined },
            { label: "TutorAddress", value: formatAddress(customer.address) },
            { label: "PetPeso", value: weight },
          ],
        },
      };
    })
    .sort(
      (firstPet, secondPet) =>
        Number(secondPet.active === true) - Number(firstPet.active === true)
    );
}

function normalizeSubscriptions(subscriptions) {
  if (!Array.isArray(subscriptions)) return [];

  return subscriptions.map((subscription = {}) => ({
    id: subscription.id || null,
    petId: subscription.petId || null,
    petName: (subscription.petName || "").trim().split(" ")[0] || "",
    planName: (subscription.planName || "").replace(/^\s*Plano\s*/i, "").trim(),
    planType: subscription.planType || "",
    status: subscription.status || "",
    startedAt: normalizeDate(subscription.startedAt),
    validAt: normalizeDate(subscription.validAt),
    nextBillingAt: normalizeDate(subscription.nextBillingAt),
    canceledAt: normalizeDate(subscription.canceledAt),
    quantityAvailable: subscription.quantityAvailable ?? null,
  }));
}

function normalizePackages(packages) {
  if (!Array.isArray(packages)) return [];

  return packages.map((packageData = {}) => ({
    id: packageData.id || null,
    petId: packageData.petId || null,
    petName: (packageData.petName || "").trim().split(" ")[0] || "",
    name: packageData.name || "",
    department: packageData.department || "",
    status: packageData.status || "",
    quantityAvailable: packageData.quantityAvailable ?? null,
    expiresAt: normalizeDate(packageData.expiresAt),
  }));
}

export function formatCustomerDetails(payload = {}) {
  const customer = payload.customer || {};

  return {
    customer: {
      ...EMPTY_CUSTOMER,
      id: customer.id || null,
      name: customer.name || "",
      cpf: customer.cpf || "",
      email: customer.email || "",
      cellphone: customer.cellphone || "",
      telephone: customer.telephone || "",
      address: customer.address || null,
      createdAt: normalizeDate(customer.createdAt),
      lastVisitAt: normalizeDate(customer.lastVisitAt),
    },
    pets: normalizePets(
      payload.pets,
      customer,
      payload.subscriptions,
      payload.activePackages
    ),
    subscriptions: normalizeSubscriptions(payload.subscriptions),
    activePackages: normalizePackages(payload.activePackages),
    serviceHistory: normalizeHistory(payload.serviceHistory, "service"),
    purchaseHistory: normalizeHistory(payload.purchaseHistory, "item"),
  };
}
